import { useState } from 'react';
import { IDocument } from '../statApi/DocumentApi';

interface IDocumentsListState {
  currentDocuments: IDocument[];
  newDocuments: IDocument[];
  removedDocuments: IDocument[];
  uploadIsInProgress: boolean;
}

export interface IUseDocumentsListResult extends IDocumentsListState {
  /** Ids of previously persisted documents the user removed. */
  documentIdsToRemove: number[];
  attachDocuments: (files: IDocument[]) => Promise<void>;
  deleteDocument: (index: number) => void;
  setUploadInProgress: (uploadIsInProgress: boolean) => void;
}

/**
 * Tracks the pending add/remove changes to a list of documents while the user edits them
 * (e.g. in a panel), so the caller can persist the diff in a single mutation on save.
 */
const useDocumentsList = (
  initialDocuments: IDocument[] | undefined,
  maxOfOne = false,
): IUseDocumentsListResult => {
  const [state, setState] = useState<IDocumentsListState>(() => ({
    currentDocuments: [...(initialDocuments || [])],
    newDocuments: [],
    removedDocuments: [],
    uploadIsInProgress: false,
  }));

  const deleteDocument = (index: number): void => {
    setState((prev) => {
      const removedDoc = prev.currentDocuments[index];
      if (!removedDoc) {
        return prev;
      }
      return {
        ...prev,
        currentDocuments: prev.currentDocuments.filter((_, i) => i !== index),
        removedDocuments:
          removedDoc.id > 0 ? [...prev.removedDocuments, removedDoc] : prev.removedDocuments,
        newDocuments: prev.newDocuments.filter((doc) => doc.url !== removedDoc.url),
      };
    });
  };

  const attachDocuments = async (files: IDocument[]): Promise<void> => {
    setState((prev) => ({
      ...prev,
      currentDocuments: maxOfOne ? [files[0]] : prev.currentDocuments.concat(files),
      newDocuments: maxOfOne ? [files[0]] : prev.newDocuments.concat(files),
      uploadIsInProgress: false,
    }));
  };

  const setUploadInProgress = (uploadIsInProgress: boolean): void => {
    setState((prev) => ({ ...prev, uploadIsInProgress }));
  };

  return {
    ...state,
    documentIdsToRemove: state.removedDocuments.map((document) => document.id),
    attachDocuments,
    deleteDocument,
    setUploadInProgress,
  };
};

export default useDocumentsList;
